import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { 
  getBlogPost, 
  likeBlogPost, 
  bookmarkBlogPost,
  addBlogComment,
  getPostComments,
  getRecentPosts,
  getBlogCategories
} from '../api/api';
import './BlogPostDetail.css';

const BlogPostDetail = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [error, setError] = useState(null);
  const [commentError, setCommentError] = useState('');
  const [commentSuccess, setCommentSuccess] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPostData();
    fetchSidebarData();
  }, [postId]);

  const fetchPostData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const postData = await getBlogPost(postId);
      const postComments = await getPostComments(postId);
      
      setPost(postData);
      setComments(postComments);
    } catch (error) {
      console.error('Error fetching post:', error);
      setError('Post not found or you may not have permission to view it.');
    } finally {
      setLoading(false);
    }
  };

  const fetchSidebarData = async () => {
    try {
      const [recent, cats] = await Promise.all([
        getRecentPosts(5),
        getBlogCategories()
      ]);
      
      setRecentPosts(recent);
      setCategories(cats);
    } catch (error) {
      console.error('Error fetching sidebar data:', error);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/blog/${postId}` } });
      return;
    }
    
    try {
      const result = await likeBlogPost(postId);
      setPost(prev => ({
        ...prev,
        user_liked: result.liked,
        likes_count: result.likes_count
      }));
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleBookmark = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/blog/${postId}` } });
      return;
    }
    
    try {
      const result = await bookmarkBlogPost(postId);
      setPost(prev => ({
        ...prev,
        user_bookmarked: result.bookmarked
      }));
    } catch (error) {
      console.error('Error bookmarking post:', error);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!commentText.trim()) {
      setCommentError('Please enter a comment');
      return;
    }
    
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/blog/${postId}` } });
      return;
    }
    
    try {
      setSubmittingComment(true);
      setCommentError('');
      setCommentSuccess('');
      
      // Create comment data with post ID (as integer, not object)
      const commentData = {
        post: parseInt(postId),  // This should be an integer
        content: commentText.trim()
      };
      
      // Add parent if replying
      if (replyTo) {
        commentData.parent = replyTo.id;
      }
      
      console.log('Submitting comment data:', commentData);
      
      // Call the API
      const response = await addBlogComment(commentData);
      
      // Add new comment to list
      if (replyTo) {
        // Add as reply
        setComments(prev => prev.map(comment => 
          comment.id === replyTo.id
            ? { ...comment, replies: [...(comment.replies || []), response] }
            : comment
        ));
      } else {
        // Add as new comment
        setComments(prev => [response, ...prev]);
      }
      
      // Clear form and show success
      setCommentText('');
      setReplyTo(null);
      setCommentSuccess(replyTo ? 'Reply posted successfully!' : 'Comment posted successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setCommentSuccess('');
      }, 3000);
      
    } catch (error) {
      console.error('Error adding comment:', error);
      
      // Show specific error message
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === 'object') {
          // Join all error messages
          const errorMessages = Object.values(errorData).flat().join(', ');
          setCommentError(`Failed to add comment: ${errorMessages}`);
        } else if (typeof errorData === 'string') {
          setCommentError(errorData);
        } else {
          setCommentError('Failed to add comment. Please try again.');
        }
      } else {
        setCommentError('Failed to add comment. Please try again.');
      }
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleReply = (comment) => {
    setReplyTo(comment);
    setCommentText(`@${comment.author.username} `);
  };

  const handleCancelReply = () => {
    setReplyTo(null);
    setCommentText('');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    
    return formatDate(dateString);
  };

  const renderComments = (commentList, isReply = false) => {
    return commentList.map(comment => (
      <div key={comment.id} className={`comment ${isReply ? 'comment-reply' : ''}`}>
        <div className="comment-header">
          <div className="comment-author">
            <strong>{comment.author?.username || 'Anonymous'}</strong>
          </div>
          <span className="comment-date" title={formatDate(comment.created_at)}>
            {getRelativeTime(comment.created_at)}
          </span>
        </div>
        
        <div className="comment-content">
          {comment.content}
        </div>
        
        <div className="comment-actions">
          {isAuthenticated && (
            <button 
              className="reply-btn"
              onClick={() => handleReply(comment)}
              disabled={submittingComment}
            >
              Reply
            </button>
          )}
        </div>
        
        {comment.replies && comment.replies.length > 0 && (
          <div className="comment-replies">
            {renderComments(comment.replies, true)}
          </div>
        )}
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="post-detail-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading post...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="post-detail-container">
        <div className="error-message">
          <h2>Post Not Found</h2>
          <p>{error}</p>
          <Link to="/blog" className="back-link">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="post-detail-container">
      <div className="post-detail-layout">
        {/* Main Content */}
        <main className="post-main-content">
          {/* Breadcrumb */}
          <nav className="breadcrumb">
            <Link to="/">Home</Link>
            <span> › </span>
            <Link to="/blog">Blog</Link>
            <span> › </span>
            <span>{post.title}</span>
          </nav>

          {/* Post Header */}
          <article className="post-article">
            <header className="post-header">
              {post.category && (
                <Link to={`/blog?category=${post.category.slug}`} className="category-link">
                  {post.category.name}
                </Link>
              )}
              
              <h1 className="post-title">{post.title}</h1>
              
              <div className="post-meta">
                <div className="author-info">
                  {post.author && (
                    <span className="author-name">{post.author.username}</span>
                  )}
                </div>
                
                <div className="post-dates">
                  <span className="publish-date">
                    Published: {formatDate(post.published_at)}
                  </span>
                  <span className="reading-time">
                    • {post.reading_time} min read
                  </span>
                </div>
              </div>
              
              {post.featured_image && (
                <div className="featured-image">
                  <img src={post.featured_image} alt={post.title} />
                  {post.image_caption && (
                    <figcaption className="image-caption">
                      {post.image_caption}
                      {post.image_credit && (
                        <span className="image-credit"> • Credit: {post.image_credit}</span>
                      )}
                    </figcaption>
                  )}
                </div>
              )}
            </header>

            {/* Post Content */}
            <div className="post-content">
              {post.content.split('\n\n').map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="post-tags">
                <strong>Tags: </strong>
                {post.tags.map(tag => (
                  <Link 
                    key={tag.id} 
                    to={`/blog?tag=${tag.slug}`}
                    className="tag"
                  >
                    #{tag.name}
                  </Link>
                ))}
              </div>
            )}

            {/* Post Actions */}
            <div className="post-actions">
              <div className="action-buttons">
                <button 
                  className={`action-btn like-btn ${post.user_liked ? 'liked' : ''}`}
                  onClick={handleLike}
                  disabled={!isAuthenticated}
                >
                  <span className="icon">❤️</span>
                  <span>Like ({post.likes_count || 0})</span>
                </button>
                
                <button 
                  className={`action-btn bookmark-btn ${post.user_bookmarked ? 'bookmarked' : ''}`}
                  onClick={handleBookmark}
                  disabled={!isAuthenticated}
                >
                  <span className="icon">🔖</span>
                  <span>Bookmark</span>
                </button>
                
                <div className="views-count">
                  👁️ {post.views || 0} views
                </div>
              </div>
              
              <div className="share-buttons">
                <button className="share-btn" onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copied to clipboard!');
                }}>
                  🔗 Share
                </button>
              </div>
            </div>
          </article>

          {/* Comments Section */}
          <section className="comments-section">
            <h2 className="section-title">
              Comments ({comments.length})
            </h2>
            
            {/* Show success/error messages */}
            {commentSuccess && (
              <div className="comment-success">
                ✅ {commentSuccess}
              </div>
            )}
            
            {commentError && (
              <div className="comment-error">
                ⚠️ {commentError}
              </div>
            )}
            
            {/* Comment Form */}
            <form onSubmit={handleCommentSubmit} className={`comment-form ${submittingComment ? 'submitting' : ''}`}>
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={
                  replyTo 
                    ? `Replying to ${replyTo.author.username}...`
                    : 'Leave a comment...'
                }
                rows="4"
                required
                disabled={submittingComment}
              />
              
              <div className="comment-form-actions">
                {replyTo && (
                  <div className="replying-to">
                    Replying to <strong>{replyTo.author.username}</strong>
                    <button 
                      type="button"
                      className="cancel-reply"
                      onClick={handleCancelReply}
                      disabled={submittingComment}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                
                <button 
                  type="submit" 
                  className="submit-comment"
                  disabled={submittingComment || !commentText.trim()}
                >
                  {submittingComment ? 'Posting...' : (replyTo ? 'Post Reply' : 'Post Comment')}
                </button>
              </div>
            </form>

            {/* Comments List */}
            <div className="comments-list">
              {comments.length === 0 ? (
                <p className="no-comments">No comments yet. Be the first to comment!</p>
              ) : (
                renderComments(comments)
              )}
            </div>
          </section>
        </main>

        {/* Sidebar */}
        <aside className="post-sidebar">
          {/* Author Bio */}
          {post.author && (
            <div className="author-bio">
              <h3>About the Author</h3>
              <div className="author-details">
                <div className="author-name">{post.author.username}</div>
                <div className="author-email">{post.author.email}</div>
              </div>
            </div>
          )}

          {/* Categories */}
          <div className="sidebar-widget">
            <h3>Categories</h3>
            <ul className="category-list">
              {categories.map(category => (
                <li key={category.id}>
                  <Link to={`/blog?category=${category.slug}`}>
                    {category.name}
                    <span className="count">({category.post_count || 0})</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Recent Posts */}
          <div className="sidebar-widget">
            <h3>Recent Posts</h3>
            <ul className="recent-posts-list">
              {recentPosts.map(recentPost => (
                <li key={recentPost.id}>
                  <Link to={`/blog/${recentPost.id}`}>
                    {recentPost.title}
                  </Link>
                  <span className="date">
                    {formatDate(recentPost.published_at)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default BlogPostDetail;